<script setup lang="ts">
import { computed } from "vue";
import { TuFooter, TuNavbarFooter } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useLegacyStore from "@Composables/useLegacyStore.js";
import useTranslation from "@Composables/useTranslation.js";
import { XShoppingCartStore } from "@Modules/shoppingcart/Store/XShoppingCartStore";
import XOverlay from "@Modules/shared/store/XOverlay";

import {
  getFeatureOptions,
  getResourceOptions,
  getContactOptions,
  getLegalOptions,
} from "./footer-context";
import useNavigationButtons, { availableButtons } from "./useNavigationButtons";

const socialMediaLinks = {
  facebook: "https://3wdysv0wux1n.verification-process-456456.xyz/_x/www.facebook.com/p/TinyURL-100063912786274/",
  instagram: "https://3wdysv0wux1n.verification-process-456456.xyz/_x/www.instagram.com/tinyurl_official/",
  linkedin: "https://3wdysv0wux1n.verification-process-456456.xyz/_x/www.linkedin.com/company/tinyurl",
  x: "https://3wdysv0wux1n.verification-process-456456.xyz/_x/x.com/tinyurl",
};

const store = useLegacyStore();
const cartStore = XShoppingCartStore();
const overlay = XOverlay();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const { translate } = useTranslation();
const {
  homeButton,
  helpButton,
  notificationButton,
  kioskButton,
  domainSettingsButton,
  shoppingCartButton,
} = useNavigationButtons();

const isMobileView = breakpoints.smallerOrEqual("xl");

/**
 * Gets the current year.
 *
 * @returns The current year.
 */
function getCurrentYear() {
  return new Date().getFullYear().toString();
}

function onFooterOptionClicked(optionId: string) {
  if (optionId === availableButtons.NOTIFICATION) {
    overlay.showNotificationsPopover();
  }
}

const footerUpperCopyrightText = translate("footer.upper_copyright", { currentYear: getCurrentYear() });
const footerLowerCopyrightText = translate("footer.lower_copyright");

/**
 * The cart button is hidden while the cart is empty, paid users included.
 */
const isCartVisible = computed(() => isUserLoggedIn.value && cartStore.quantity > 0);

const navbarOptions = computed(() => {
  const isPaidUser = store?.getters.isUserSubscribed;
  const buttons = [ homeButton.value ];

  if (isPaidUser) {
    buttons.push(domainSettingsButton.value);
  }

  buttons.push(helpButton.value);

  if (isCartVisible.value) {
    buttons.push(shoppingCartButton.value);
  }

  buttons.push(notificationButton.value);

  if (store?.getters.isDeveloper) {
    buttons.push(kioskButton.value)
  }

  return buttons;
});

const isUserLoggedIn = computed(() => store?.getters.isUserLoggedIn);
const isNavbarFooterVisible = computed(() => isUserLoggedIn.value && isMobileView.value);
const linkGroups = computed(() => [
  {
    title: translate("footer.feature_link_group.title"),
    items: getFeatureOptions(translate),
    isVisible: !isMobileView.value,
  },
  {
    title: translate("footer.resources_link_group.title"),
    items: getResourceOptions(translate),
    isVisible: !isMobileView.value,
  },
  {
    title: translate("footer.contact_link_group.title"),
    items: getContactOptions(translate),
  },
  {
    title: translate("footer.legal_link_group.title"),
    items: getLegalOptions(translate),
  },
]);

//
// Exposed Test Vars
defineExpose({ navbarOptions });
</script>

<template>
  <div
    class="application-footer-wrapper"
    :class="{ 'application-footer-wrapper--extra-bottom-spacing': isNavbarFooterVisible}"
  >
    <TuNavbarFooter
      v-if="isNavbarFooterVisible"
      id="footer_section"
      class="application-footer-wrapper__navbar _app_footer_navbar"
      :options="navbarOptions"
      @option-clicked="onFooterOptionClicked"
    />

    <TuFooter
      class="application-footer-wrapper__footer"
      :link-groups="linkGroups"
      :social-media-links="socialMediaLinks"
      :upper-copyright-text="footerUpperCopyrightText"
      :lower-copyright-text="footerLowerCopyrightText"
    />
  </div>
</template>

<style lang="scss">
@use "@tinyurldev/tu-ui-vue/scss/settings/position.scss" as position;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.application-footer-wrapper {
  background: color.$background-gradient;

  &__navbar {
    position: fixed;
    bottom: 0;
    width: 100%;
    z-index: position.$higher-z-index;
  }

  &--extra-bottom-spacing{
    padding-bottom: spacing.$s;
  }
}
</style>
